import React from 'react';
import { Calendar, CalendarDays, Clock, ArrowRight } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для быстрого выбора дат
 */
const QuickDatesSidebar = ({ 
  formData,
  setFormData,
  setResults,
  errors,
  isCalculating
}) => {
  
  // Текущая дата
  const today = new Date();
  
  // Предопределенные даты для быстрого выбора
  const quickDates = [
    { 
      title: 'Сегодня', 
      value: today.toISOString().split('T')[0],
      icon: <Calendar className="w-4 h-4 text-cyan-600 dark:text-cyan-400" /> 
    },
    { 
      title: 'Вчера', 
      value: new Date(today.setDate(today.getDate() - 1)).toISOString().split('T')[0],
      icon: <Calendar className="w-4 h-4 text-cyan-600 dark:text-cyan-400" /> 
    },
    { 
      title: 'Неделю назад', 
      value: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
      icon: <CalendarDays className="w-4 h-4 text-cyan-600 dark:text-cyan-400" /> 
    },
    { 
      title: 'Месяц назад', 
      value: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
      icon: <CalendarDays className="w-4 h-4 text-cyan-600 dark:text-cyan-400" /> 
    },
    { 
      title: 'Начало года', 
      value: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
      icon: <Calendar className="w-4 h-4 text-cyan-600 dark:text-cyan-400" /> 
    },
    { 
      title: 'Год назад', 
      value: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0],
      icon: <CalendarDays className="w-4 h-4 text-cyan-600 dark:text-cyan-400" /> 
    }
  ];
  
  // Предопределенные промежутки времени
  const quickRanges = [
    { title: 'Текущая неделя', start: getStartOfWeek(), end: new Date().toISOString().split('T')[0] },
    { title: 'Текущий месяц', start: getStartOfMonth(), end: new Date().toISOString().split('T')[0] },
    { title: 'Текущий квартал', start: getStartOfQuarter(), end: new Date().toISOString().split('T')[0] },
    { title: 'Текущий год', start: getStartOfYear(), end: new Date().toISOString().split('T')[0] },
    { title: 'Последние 30 дней', start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0], end: new Date().toISOString().split('T')[0] },
    { title: 'Последние 90 дней', start: new Date(new Date().setDate(new Date().getDate() - 90)).toISOString().split('T')[0], end: new Date().toISOString().split('T')[0] },
    { title: 'Последние 365 дней', start: new Date(new Date().setDate(new Date().getDate() - 365)).toISOString().split('T')[0], end: new Date().toISOString().split('T')[0] },
  ];
  
  // Популярные промежутки для добавления/вычитания
  const quickPeriods = [
    { title: '1 неделя (7 дней)', period: 7, type: 'days' },
    { title: '2 недели (14 дней)', period: 14, type: 'days' },
    { title: '1 месяц', period: 1, type: 'months' },
    { title: '3 месяца (квартал)', period: 3, type: 'months' },
    { title: '6 месяцев (полгода)', period: 6, type: 'months' },
    { title: '1 год', period: 1, type: 'years' },
    { title: '5 лет', period: 5, type: 'years' },
    { title: '10 лет', period: 10, type: 'years' },
  ];
  
  // Функция получения начала текущей недели (понедельник)
  function getStartOfWeek() {
    const date = new Date();
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1); // поправка для воскресенья
    return new Date(date.setDate(diff)).toISOString().split('T')[0];
  }
  
  // Функция получения начала текущего месяца
  function getStartOfMonth() {
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
  }
  
  // Функция получения начала текущего квартала
  function getStartOfQuarter() {
    const month = Math.floor(new Date().getMonth() / 3) * 3;
    return new Date(new Date().getFullYear(), month, 1).toISOString().split('T')[0];
  }
  
  // Функция получения начала текущего года
  function getStartOfYear() {
    return new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0];
  }
  
  // Применение быстрой даты к начальной дате
  const applyQuickDate = (dateValue) => {
    setFormData(prev => {
      // Для калькулятора возраста применяем к дате рождения
      if (prev.calculationType === 'age') {
        return { ...prev, birthdayDate: dateValue };
      }
      
      // Для остальных - к начальной дате
      return { ...prev, startDate: dateValue };
    });
    setResults(null);
  };
  
  // Применение быстрого промежутка
  const applyQuickRange = (startDate, endDate) => {
    setFormData(prev => ({
      ...prev,
      startDate,
      endDate,
      calculationType: prev.calculationType === 'add-subtract' || prev.calculationType === 'age' ? 'standard' : prev.calculationType
    }));
    setResults(null);
  };
  
  // Применение периода для добавления/вычитания
  const applyQuickPeriod = (periodValue, typeValue) => {
    setFormData(prev => ({
      ...prev,
      calculationType: 'add-subtract',
      datePeriod: periodValue,
      periodType: typeValue
    }));
    setResults(null);
  };
  
  // Заголовок в зависимости от типа расчета
  const getSidebarTitle = () => {
    switch (formData.calculationType) {
      case 'standard':
        return 'Популярные промежутки';
      case 'working':
        return 'Календарные периоды';
      case 'add-subtract':
        return 'Типовые сроки';
      case 'age':
        return 'Типовые даты рождения';
      default:
        return 'Быстрый выбор дат';
    }
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Clock className="w-5 h-5 mr-2 text-cyan-500" />
        {getSidebarTitle()}
      </h3>
      
      <div className="space-y-4">
        {/* Быстрый выбор даты (для всех типов) */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Выбрать дату
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {quickDates.map((date, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => applyQuickDate(date.value)}
                className="flex items-center text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {date.icon}
                <span className="ml-2">{date.title}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Быстрые промежутки времени (для стандартного расчета и рабочих дней) */}
        {(formData.calculationType === 'standard' || formData.calculationType === 'working') && (
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Промежутки времени
            </h4>
            <div className="space-y-2">
              {quickRanges.map((range, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => applyQuickRange(range.start, range.end)}
                  className="w-full flex items-center justify-between text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <span>{range.title}</span>
                  <ArrowRight className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Быстрые периоды (для добавления/вычитания) */}
        {(formData.calculationType === 'add-subtract' || formData.calculationType === 'standard') && (
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Типовые периоды
            </h4>
            <div className="space-y-2">
              {quickPeriods.map((periodItem, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => applyQuickPeriod(periodItem.period, periodItem.type)}
                  className="w-full flex items-center justify-between text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <span>{periodItem.title}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {periodItem.period} {periodItem.type}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Кнопка сброса */}
        <div className="pt-2">
          <Button
            onClick={() => {
              const today = new Date().toISOString().split('T')[0];
              setFormData(prev => ({
                ...prev,
                startDate: today,
                endDate: today,
                includeEnd: true
              }));
              setResults(null);
            }}
            variant="outline"
            fullWidth
          >
            Сбросить даты
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickDatesSidebar;